/**
 * Services Section Component
 *
 * React implementation of the services section renderer.
 * Supports variants: grid, list, pricing, accordion
 */

import { ServicesData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface ServicesProps extends SectionProps<ServicesData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Services({ data, ctx, sectionId, elements }: ServicesProps) {
  const variant = data.variant || 'grid';

  switch (variant) {
    case 'list':
      return <ServicesList data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'pricing':
      return <ServicesPricing data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'accordion':
      return <ServicesAccordion data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <ServicesGrid data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// GRID VARIANT
// =============================================================================

function ServicesGrid({ data, ctx, elements }: ServicesProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const cardBgClass = isDark ? 'bg-gray-800' : 'bg-gray-50';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const eyebrowEl = findElement(elements, 'eyebrow');

  return (
    <section id="services" className={`py-20 md:py-28 ${bgClass}`} style={{ paddingTop: recipe.spacing.sectionPaddingY, paddingBottom: recipe.spacing.sectionPaddingY }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {data.eyebrow && eyebrowEl && (
            <EditableText
              element={eyebrowEl}
              as="p"
              className="text-sm font-semibold tracking-wider uppercase mb-4"
              style={{ color: recipe.colors.primary }}
            />
          )}
          {data.eyebrow && !eyebrowEl && (
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
              {data.eyebrow}
            </p>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            />
          ) : (
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            >
              {data.headline}
            </h2>
          )}

          {data.subheadline && subheadlineEl && (
            <EditableText
              element={subheadlineEl}
              as="p"
              className={`text-lg ${mutedClass}`}
              style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
            />
          )}
          {data.subheadline && !subheadlineEl && (
            <p className={`text-lg ${mutedClass}`} style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}>
              {data.subheadline}
            </p>
          )}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.services.map((service, index) => {
            const serviceNameEl = findElement(elements, `services.${index}.name`);
            const serviceDescEl = findElement(elements, `services.${index}.description`);
            const serviceImageEl = findElement(elements, `services.${index}.image`);

            return (
              <div
                key={index}
                className={`${cardBgClass} p-6 transition-all hover:shadow-lg`}
                style={{
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {service.image && (
                  <div className="mb-4 overflow-hidden" style={{ borderRadius: recipe.borders.imageRadius }}>
                    {serviceImageEl ? (
                      <EditableImage
                        element={serviceImageEl}
                        alt={service.name}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-48 object-cover"
                      />
                    )}
                  </div>
                )}

                {service.icon && (
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-2xl"
                    style={{ backgroundColor: `${recipe.colors.primary}20`, color: recipe.colors.primary }}
                  >
                    {service.icon}
                  </div>
                )}

                {serviceNameEl ? (
                  <EditableText
                    element={serviceNameEl}
                    as="h3"
                    className={`text-xl font-semibold ${textClass} mb-2`}
                    style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
                  />
                ) : (
                  <h3
                    className={`text-xl font-semibold ${textClass} mb-2`}
                    style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
                  >
                    {service.name}
                  </h3>
                )}

                {serviceDescEl ? (
                  <EditableText
                    element={serviceDescEl}
                    as="p"
                    className={`${mutedClass} mb-4`}
                  />
                ) : (
                  <p className={`${mutedClass} mb-4`}>
                    {service.description}
                  </p>
                )}

                {service.price && (
                  <p className={`text-lg font-semibold ${textClass}`}>
                    {service.price}
                    {service.priceNote && (
                      <span className={`text-sm font-normal ${mutedClass}`}> {service.priceNote}</span>
                    )}
                  </p>
                )}

                {service.cta && (
                  <a
                    href={service.cta.url}
                    className="inline-flex items-center mt-4 font-medium transition-colors"
                    style={{ color: recipe.colors.primary }}
                  >
                    {service.cta.text}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Section CTA */}
        {data.cta && (
          <div className="text-center mt-12">
            <a
              href={data.cta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
                boxShadow: recipe.shadows.button,
              }}
            >
              {data.cta.text}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// LIST VARIANT
// =============================================================================

function ServicesList({ data, ctx, elements }: ServicesProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const cardBgClass = isDark ? 'bg-gray-800' : 'bg-white';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section id="services" className={`py-20 md:py-28 ${bgClass}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {data.eyebrow && (
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
              {data.eyebrow}
            </p>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            />
          ) : (
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            >
              {data.headline}
            </h2>
          )}

          {data.subheadline && subheadlineEl && (
            <EditableText
              element={subheadlineEl}
              as="p"
              className={`text-lg ${mutedClass}`}
            />
          )}
          {data.subheadline && !subheadlineEl && (
            <p className={`text-lg ${mutedClass}`}>
              {data.subheadline}
            </p>
          )}
        </div>

        {/* Services List */}
        <div className="space-y-6">
          {data.services.map((service, index) => (
            <div
              key={index}
              className={`${cardBgClass} p-6 flex gap-6 items-start`}
              style={{
                borderRadius: recipe.borders.cardRadius,
                boxShadow: recipe.shadows.card,
              }}
            >
              {service.icon && (
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl"
                  style={{ backgroundColor: `${recipe.colors.primary}20`, color: recipe.colors.primary }}
                >
                  {service.icon}
                </div>
              )}
              <div className="flex-1">
                <h3
                  className={`text-xl font-semibold ${textClass} mb-2`}
                  style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
                >
                  {service.name}
                </h3>
                <p className={`${mutedClass}`}>
                  {service.description}
                </p>
                {service.features && service.features.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className={`flex items-center gap-2 ${mutedClass}`}>
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: recipe.colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {service.price && (
                <div className="text-right flex-shrink-0">
                  <p className={`text-xl font-bold ${textClass}`}>{service.price}</p>
                  {service.priceNote && (
                    <p className={`text-sm ${mutedClass}`}>{service.priceNote}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// PRICING VARIANT
// =============================================================================

function ServicesPricing({ data, ctx, elements }: ServicesProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const cardBgClass = isDark ? 'bg-gray-800' : 'bg-gray-50';

  const headlineEl = findElement(elements, 'headline');

  return (
    <section id="services" className={`py-20 md:py-28 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {data.eyebrow && (
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
              {data.eyebrow}
            </p>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            />
          ) : (
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            >
              {data.headline}
            </h2>
          )}

          {data.subheadline && (
            <p className={`text-lg ${mutedClass}`}>
              {data.subheadline}
            </p>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.services.map((service, index) => (
            <div
              key={index}
              className={`${cardBgClass} p-8 text-center relative`}
              style={{
                borderRadius: recipe.borders.cardRadius,
                boxShadow: recipe.shadows.card,
              }}
            >
              {service.icon && (
                <div
                  className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6 text-3xl"
                  style={{ backgroundColor: `${recipe.colors.primary}20`, color: recipe.colors.primary }}
                >
                  {service.icon}
                </div>
              )}

              <h3
                className={`text-2xl font-semibold ${textClass} mb-2`}
                style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
              >
                {service.name}
              </h3>

              {service.price && (
                <div className="my-6">
                  <span className={`text-4xl font-bold ${textClass}`}>{service.price}</span>
                  {service.priceNote && (
                    <span className={`text-sm ${mutedClass}`}> {service.priceNote}</span>
                  )}
                </div>
              )}

              <p className={`${mutedClass} mb-6`}>
                {service.description}
              </p>

              {service.features && service.features.length > 0 && (
                <ul className="space-y-3 mb-8 text-left">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className={`flex items-center gap-3 ${mutedClass}`}>
                      <svg className="w-5 h-5 flex-shrink-0" style={{ color: recipe.colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              )}

              {service.cta && (
                <a
                  href={service.cta.url}
                  className="w-full inline-flex items-center justify-center px-6 py-3 font-semibold text-white transition-all hover:opacity-90"
                  style={{
                    backgroundColor: recipe.colors.primary,
                    borderRadius: recipe.borders.buttonRadius,
                  }}
                >
                  {service.cta.text}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// ACCORDION VARIANT
// =============================================================================

function ServicesAccordion({ data, ctx, elements }: ServicesProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-200';

  const headlineEl = findElement(elements, 'headline');

  return (
    <section id="services" className={`py-20 md:py-28 ${bgClass}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {data.eyebrow && (
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
              {data.eyebrow}
            </p>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            />
          ) : (
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-${recipe.typography.headingWeight} ${textClass} mb-4`}
              style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
            >
              {data.headline}
            </h2>
          )}

          {data.subheadline && (
            <p className={`text-lg ${mutedClass}`}>
              {data.subheadline}
            </p>
          )}
        </div>

        {/* Accordion */}
        <div className={`divide-y ${borderClass}`}>
          {data.services.map((service, index) => (
            <details key={index} className="group py-6">
              <summary className={`flex items-center justify-between cursor-pointer list-none ${textClass}`}>
                <div className="flex items-center gap-4">
                  {service.icon && (
                    <span className="text-2xl" style={{ color: recipe.colors.primary }}>
                      {service.icon}
                    </span>
                  )}
                  <h3 className="text-xl font-semibold" style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}>
                    {service.name}
                  </h3>
                </div>
                <svg
                  className="w-6 h-6 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className={`mt-4 pl-12 ${mutedClass}`}>
                <p className="mb-4">{service.description}</p>
                {service.features && service.features.length > 0 && (
                  <ul className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: recipe.colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
                {service.cta && (
                  <a
                    href={service.cta.url}
                    className="inline-flex items-center mt-4 font-medium transition-colors"
                    style={{ color: recipe.colors.primary }}
                  >
                    {service.cta.text}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Services;
